<script setup lang="ts">
import { object, string, number } from 'yup'
import type { ReviewResponse } from '@/types'

const props = defineProps<{
  clinicId: string
  clinicSlug: string
  clinicName: string
  existingReview?: ReviewResponse | null
}>()

const emit = defineEmits<{
  success: [review: ReviewResponse]
}>()

const open = defineModel<boolean>('open', { default: false })

const route = useRoute()
const { isAuthenticated } = useAuth()
const { user } = useUser()
const { capture } = useAnalytics()
const { createReview, updateReview, isLoading, error, clearError } = useReviews(props.clinicId)
const { save: savePendingReview } = usePendingReview()

const step = ref<'form' | 'auth'>('form')
const isEditing = computed(() => !!props.existingReview)

const schema = object({
  rating: number()
    .integer('Hodnocení musí být celé číslo')
    .min(1, 'Vyberte hodnocení (1-5 hvězdiček)')
    .max(5, 'Hodnocení může být maximálně 5')
    .required('Vyberte hodnocení'),
  comment: string()
    .max(2000, 'Komentář může mít maximálně 2000 znaků')
    .nullable(),
})

const state = reactive({
  rating: props.existingReview?.rating ?? (null as unknown as number),
  comment: props.existingReview?.comment ?? '',
})

const prosItems = ref<string[]>([''])
const consItems = ref<string[]>([''])

const ratingLabels: Record<number, string> = {
  1: 'Velmi špatné',
  2: 'Špatné',
  3: 'Průměrné',
  4: 'Dobré',
  5: 'Výborné',
}
const ratingLabel = computed(() => ratingLabels[state.rating] ?? '')

const formRef = useTemplateRef('reviewForm')

function parseItemList(text: string | null): string[] {
  if (!text) return ['']
  const items = text.split('\n').map((s) => s.trim()).filter(Boolean)
  return items.length > 0 ? items : ['']
}

function joinItemList(items: string[]): string | undefined {
  const filtered = items.map((s) => s.trim()).filter(Boolean)
  return filtered.length > 0 ? filtered.join('\n') : undefined
}

function addPro() {
  if (prosItems.value.length < 5) prosItems.value.push('')
}

function removePro(index: number) {
  prosItems.value.splice(index, 1)
  if (prosItems.value.length === 0) prosItems.value.push('')
}

function addCon() {
  if (consItems.value.length < 5) consItems.value.push('')
}

function removeCon(index: number) {
  consItems.value.splice(index, 1)
  if (consItems.value.length === 0) consItems.value.push('')
}

watch(open, (isOpen) => {
  if (isOpen) {
    step.value = 'form'
    clearError()
    if (props.existingReview) {
      state.rating = props.existingReview.rating as unknown as number
      state.comment = props.existingReview.comment ?? ''
      prosItems.value = parseItemList(props.existingReview.pros)
      consItems.value = parseItemList(props.existingReview.cons)
    } else {
      state.rating = null as unknown as number
      state.comment = ''
      prosItems.value = ['']
      consItems.value = ['']
    }
  }
})

async function handleSubmit() {
  try {
    await formRef.value?.validate({})
  } catch {
    return
  }

  clearError()

  const data = {
    rating: state.rating!,
    comment: state.comment || undefined,
    pros: joinItemList(prosItems.value),
    cons: joinItemList(consItems.value),
  }

  if (!isAuthenticated.value) {
    savePendingReview({
      clinicId: props.clinicId,
      clinicSlug: props.clinicSlug,
      rating: data.rating,
      comment: data.comment,
      pros: data.pros,
      cons: data.cons,
    })
    localStorage.setItem('oauth_return_to', route.fullPath)
    capture('review_auth_prompted', { clinic_id: props.clinicId, rating: state.rating, has_comment: !!state.comment })
    step.value = 'auth'
    return
  }

  let result: ReviewResponse | null

  if (isEditing.value && props.existingReview) {
    result = await updateReview(props.existingReview.id, data)
  } else {
    result = await createReview(data)
  }

  if (result) {
    capture('review_submitted', { clinic_id: props.clinicId, rating: state.rating, has_comment: !!state.comment, is_edit: isEditing.value, was_pending: false })
    emit('success', result)
    open.value = false
  }
}

function handleProviderClick(provider: 'google' | 'facebook') {
  capture('review_auth_method_clicked', { clinic_id: props.clinicId, method: provider })
}

function handleEmailLoginClick() {
  capture('review_auth_method_clicked', { clinic_id: props.clinicId, method: 'email_login' })
  closeModal()
}

function handleEmailRegisterClick() {
  capture('review_auth_method_clicked', { clinic_id: props.clinicId, method: 'email_register' })
  closeModal()
}

function closeModal() {
  open.value = false
}
</script>

<template>
  <UModal
    v-model:open="open"
    :title="isEditing ? 'Upravit hodnocení' : 'Napsat hodnocení'"
    :ui="{ footer: 'justify-end' }"
  >
    <slot />

    <template #body>
      <!-- Form step -->
      <UForm
        v-if="step === 'form'"
        ref="reviewForm"
        :schema="schema"
        :state="state"
        :validate-on="[]"
        class="space-y-5"
      >
        <div class="text-sm text-muted mb-4">
          Hodnotíte: <span class="font-medium text-highlighted">{{ clinicName }}</span>
        </div>

        <div v-if="isAuthenticated && user && !user.name" class="flex items-start gap-2 p-3 bg-blue-50 rounded-lg mb-1">
          <UIcon name="i-lucide-info" class="size-4 text-blue-500 shrink-0 mt-0.5" />
          <span class="text-xs text-blue-700">
            Vaše hodnocení bude zobrazeno jako „Anonymní zákazník".
            <NuxtLink to="/administrace/nastaveni" class="underline font-medium" @click="closeModal">Vyplňte jméno v nastavení</NuxtLink>,
            aby se zobrazilo u vašeho hodnocení.
          </span>
        </div>

        <UFormField label="Hodnocení" name="rating" required>
          <div class="pt-1">
            <RatingInput v-model="state.rating" size="lg" />
            <p class="text-xs mt-1.5" :class="state.rating ? 'text-highlighted font-medium' : 'text-dimmed'">
              {{ !state.rating ? 'Klikněte na hvězdičku' : ratingLabel }}
            </p>
          </div>
        </UFormField>

        <UFormField label="Komentář" name="comment">
          <UTextarea
            v-model="state.comment"
            placeholder="Napište svůj celkový dojem z návštěvy..."
            :rows="4"
            class="w-full"
          />
          <template #hint>
            <span class="text-xs text-dimmed">{{ state.comment?.length || 0 }} / 2000</span>
          </template>
        </UFormField>

        <!-- Pros - itemized -->
        <div>
          <label class="block text-sm font-medium text-highlighted mb-2">Klady</label>
          <div class="space-y-2">
            <div v-for="(_, index) in prosItems" :key="`pro-${index}`" class="flex items-center gap-2">
              <UIcon name="i-lucide-plus-circle" class="w-4 h-4 text-green-500 shrink-0" />
              <UInput
                v-model="prosItems[index]"
                :placeholder="index === 0 ? 'Co se vám líbilo?' : 'Další klad...'"
                class="flex-1"
                size="sm"
              />
              <UButton
                v-if="prosItems.length > 1"
                color="neutral"
                variant="ghost"
                size="xs"
                icon="i-lucide-x"
                aria-label="Odebrat"
                @click="removePro(index)"
              />
            </div>
          </div>
          <UButton
            v-if="prosItems.length < 5"
            color="neutral"
            variant="link"
            size="xs"
            icon="i-lucide-plus"
            label="Přidat klad"
            class="mt-1"
            @click="addPro"
          />
        </div>

        <!-- Cons - itemized -->
        <div>
          <label class="block text-sm font-medium text-highlighted mb-2">Zápory</label>
          <div class="space-y-2">
            <div v-for="(_, index) in consItems" :key="`con-${index}`" class="flex items-center gap-2">
              <UIcon name="i-lucide-minus-circle" class="w-4 h-4 text-red-500 shrink-0" />
              <UInput
                v-model="consItems[index]"
                :placeholder="index === 0 ? 'Co by se dalo zlepšit?' : 'Další zápor...'"
                class="flex-1"
                size="sm"
              />
              <UButton
                v-if="consItems.length > 1"
                color="neutral"
                variant="ghost"
                size="xs"
                icon="i-lucide-x"
                aria-label="Odebrat"
                @click="removeCon(index)"
              />
            </div>
          </div>
          <UButton
            v-if="consItems.length < 5"
            color="neutral"
            variant="link"
            size="xs"
            icon="i-lucide-plus"
            label="Přidat zápor"
            class="mt-1"
            @click="addCon"
          />
        </div>

        <div v-if="error" class="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
          <UIcon name="i-lucide-alert-circle" class="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <span class="text-sm text-red-700">{{ error }}</span>
        </div>
      </UForm>

      <!-- Auth step -->
      <div v-else-if="step === 'auth'" class="space-y-5">
        <div class="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
          <UIcon name="i-lucide-check-circle" class="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <div class="text-sm text-green-700">
            Vaše hodnocení je uloženo. Pro odeslání se přihlaste nebo vytvořte účet.
          </div>
        </div>

        <OAuthButtons @provider-click="handleProviderClick" />

        <USeparator label="nebo" />

        <div class="flex flex-col gap-2 text-sm text-center">
          <NuxtLink
            :to="`/prihlaseni?redirect=${encodeURIComponent(route.fullPath)}`"
            class="text-primary hover:underline font-medium"
            @click="handleEmailLoginClick"
          >
            Přihlásit se e-mailem
          </NuxtLink>
          <NuxtLink
            :to="`/registrace?redirect=${encodeURIComponent(route.fullPath)}`"
            class="text-muted hover:underline"
            @click="handleEmailRegisterClick"
          >
            Vytvořit nový účet
          </NuxtLink>
        </div>
      </div>
    </template>

    <template #footer>
      <template v-if="step === 'form'">
        <UButton
          color="neutral"
          variant="outline"
          label="Zrušit"
          @click="closeModal"
        />
        <UButton
          color="primary"
          :label="isEditing ? 'Uložit změny' : 'Odeslat hodnocení'"
          :loading="isLoading"
          :disabled="!state.rating"
          @click="handleSubmit"
        />
      </template>
      <template v-else>
        <UButton
          color="neutral"
          variant="outline"
          label="Zpět"
          icon="i-lucide-arrow-left"
          @click="step = 'form'"
        />
        <UButton
          color="neutral"
          label="Zavřít"
          @click="closeModal"
        />
      </template>
    </template>
  </UModal>
</template>
